import { applySelectedTaskIdsToTasks, getUniqueExistingTaskIds } from '../syberryTodayTaskSelectionUtils';
import { SyberryTodayPersonalBacklogCategory, SyberryTodayPlanStatus } from '../types';
import {
  SyberryTodayPlanningActionType,
  type SyberryTodayPlanningAction,
  type SyberryTodayPlanningState,
} from './types';
import { mergeStateWithPlanning } from './utils';

export const syberryTodayPlanningReducer = (
  state: SyberryTodayPlanningState,
  action: SyberryTodayPlanningAction,
): SyberryTodayPlanningState => {
  switch (action.type) {
    case SyberryTodayPlanningActionType.SYNC_PLANNING:
      if (!action.planning) return state;

      return mergeStateWithPlanning(state, action.planning, action.syncOptions);

    case SyberryTodayPlanningActionType.SET_ACTIVE_GROUP:
      return {
        ...state,
        activeGroupId: action.groupId ?? null,
      };

    case SyberryTodayPlanningActionType.SELECT_TASK: {
      if (!action.taskId) return state;

      if (state.selectedTaskIds.includes(action.taskId)) {
        return state;
      }

      const selectedTaskIds = getUniqueExistingTaskIds(state.tasks, [...state.selectedTaskIds, action.taskId]);

      return {
        ...state,
        tasks: applySelectedTaskIdsToTasks(state.tasks, selectedTaskIds),
        selectedTaskIds,
      };
    }

    case SyberryTodayPlanningActionType.DESELECT_TASK: {
      if (!action.taskId) return state;

      const selectedTaskIds = state.selectedTaskIds.filter(taskId => taskId !== action.taskId);

      return {
        ...state,
        tasks: applySelectedTaskIdsToTasks(state.tasks, selectedTaskIds),
        selectedTaskIds,
        expandedTaskId: state.expandedTaskId === action.taskId ? null : state.expandedTaskId,
      };
    }

    case SyberryTodayPlanningActionType.TOGGLE_TASK_SELECTION:
      if (!action.taskId) return state;

      return syberryTodayPlanningReducer(state, {
        type: state.selectedTaskIds.includes(action.taskId)
          ? SyberryTodayPlanningActionType.DESELECT_TASK
          : SyberryTodayPlanningActionType.SELECT_TASK,
        taskId: action.taskId,
      });

    case SyberryTodayPlanningActionType.BACK_TO_TASK_SELECTION:
      return {
        ...state,
        planStatus: SyberryTodayPlanStatus.SELECTING,
      };

    case SyberryTodayPlanningActionType.TOGGLE_TASK_EXPANSION: {
      if (!action.taskId) return state;

      if (state.expandedTaskId === action.taskId) {
        return {
          ...state,
          expandedTaskId: null,
        };
      }

      const expandedTask = state.tasks.find(task => task.id === action.taskId);

      if (!expandedTask?.isUnread) {
        return {
          ...state,
          expandedTaskId: action.taskId,
        };
      }

      return {
        ...state,
        tasks: state.tasks.map(task =>
          task.id === action.taskId
            ? {
                ...task,
                isUnread: false,
                personalBacklogCategory:
                  task.personalBacklogCategory === SyberryTodayPersonalBacklogCategory.INBOX
                    ? SyberryTodayPersonalBacklogCategory.ALL
                    : task.personalBacklogCategory,
              }
            : task,
        ),
        groups: state.groups.map(group =>
          group.id === expandedTask.groupId ? { ...group, unreadCount: Math.max(group.unreadCount - 1, 0) } : group,
        ),
        expandedTaskId: action.taskId,
      };
    }

    case SyberryTodayPlanningActionType.OPEN_SKIP_CHECK_IN_MODAL:
      return {
        ...state,
        isSkipCheckInModalOpen: true,
      };

    case SyberryTodayPlanningActionType.CLOSE_SKIP_CHECK_IN_MODAL:
      return {
        ...state,
        isSkipCheckInModalOpen: false,
      };

    default:
      return state;
  }
};
